import { NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import UserSelect from "@/components/UserSelect";
import {
  LayoutDashboard, Users, ClipboardList, UserCircle2, BadgeCheck,
  UserMinus, ShieldCheck, Sparkles, Bell, Building2, ChevronDown, CalendarDays, Target, Settings as SettingsIcon, MessageSquareWarning, RefreshCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", roles: ["super_admin", "hr_manager", "recruiter", "branch_manager"] },
  { to: "/candidates", label: "Candidates", icon: Users, testid: "nav-candidates", roles: ["super_admin", "hr_manager", "recruiter", "branch_manager"] },
  { to: "/requisitions", label: "Requisitions", icon: ClipboardList, testid: "nav-requisitions", roles: ["super_admin", "hr_manager", "branch_manager"] },
  { to: "/employees", label: "Employees", icon: UserCircle2, testid: "nav-employees", roles: ["super_admin", "hr_manager", "branch_manager"] },
  { to: "/reviews", label: "Reviews", icon: BadgeCheck, testid: "nav-reviews", roles: ["super_admin", "hr_manager", "branch_manager"] },
  { to: "/attrition", label: "Attrition / Exit", icon: UserMinus, testid: "nav-attrition", roles: ["super_admin", "hr_manager", "branch_manager"] },
  { to: "/concerns", label: "Concerns", icon: MessageSquareWarning, testid: "nav-concerns", roles: ["super_admin", "hr_manager", "branch_manager"] },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance", roles: ["super_admin", "hr_manager"] },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule", roles: ["super_admin", "hr_manager", "recruiter", "branch_manager"] },
  { to: "/goals", label: "Goals", icon: Target, testid: "nav-goals", roles: ["super_admin", "hr_manager", "recruiter", "branch_manager"] },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai", roles: ["super_admin", "hr_manager", "recruiter", "branch_manager"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", roles: ["super_admin", "hr_manager"] },
];

const ROLE_LABEL = {
  super_admin: "Founder",
  hr_manager: "HR Manager",
  recruiter: "Recruiter",
  branch_manager: "Branch Manager",
};

export default function Layout() {
  const { user, branch, changeBranch, hrSelf, clearHR } = useAuth();
  const [branches, setBranches] = useState(["All", "Sarita Vihar", "Mathura", "Jaipur", "Institute"]);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta");
        setBranches(["All", ...data.branches]);
      } catch { /* ignore */ }
      try {
        const [{ data: kpi }, { data: comp }] = await Promise.all([
          api.get("/dashboard/kpis"),
          api.get("/compliance"),
        ]);
        const list = [];
        if (kpi?.top_cards?.needing_checkin) {
          list.push({ label: `${kpi.top_cards.needing_checkin} employees need a check-in`, tone: "amber" });
        }
        if (kpi?.top_cards?.docs_pending) {
          list.push({ label: `${kpi.top_cards.docs_pending} employees have pending documents`, tone: "amber" });
        }
        const overdue = (comp || []).filter((c) => c.status === "Overdue").length;
        if (overdue) list.push({ label: `${overdue} compliance items overdue`, tone: "red" });
        try {
          const { data: exp } = await api.get("/documents/expiring?days=30");
          const expired = (exp.items || []).filter((i) => i.expired).length;
          const soon = (exp.items || []).filter((i) => !i.expired).length;
          if (expired) list.push({ label: `${expired} document(s) already expired`, tone: "red" });
          if (soon) list.push({ label: `${soon} document(s) expiring in 30 days`, tone: "amber" });
        } catch { /* ignore */ }
        setNotifs(list);
      } catch { /* ignore */ }
    })();
  }, []);

  const scoped = user?.role === "branch_manager";

  return (
    <div className="min-h-screen flex bg-[var(--yc-bg)]">
      <UserSelect />
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[var(--yc-navy)] text-slate-200 flex flex-col border-r border-[var(--yc-navy-soft)]">
        <div className="px-5 py-6 border-b border-[var(--yc-navy-soft)]">
          <div className="flex items-center gap-3">
            <span className="brand-mark">YC</span>
            <div>
              <div className="font-heading font-bold text-white leading-tight">Youngness</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">by Dr. Awish</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors
                ${isActive
                  ? "bg-[var(--yc-navy-soft)] text-white border-[var(--yc-gold)]"
                  : "text-slate-400 border-transparent hover:text-white hover:bg-[var(--yc-navy-soft)]"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[var(--yc-navy-soft)] text-[11px] text-slate-500">
          <div>v1.0 · Feb 2026</div>
          <div className="mt-1">Confidential — HR Console</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-6">
            <div>
              <div className="label-eyebrow">Workspace</div>
              <div className="font-heading font-semibold text-[var(--yc-navy)] text-sm">
                Youngness Centre HR &amp; Recruitment
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={scoped}>
                <Button
                  variant="outline"
                  data-testid="branch-selector"
                  className="h-9 rounded-full border-slate-200 gap-2"
                >
                  <Building2 className="h-4 w-4 text-[var(--yc-emerald)]" />
                  <span className="font-medium">{scoped ? user.branch : branch}</span>
                  {!scoped && <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuLabel>Switch branch</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {branches.map((b) => (
                  <DropdownMenuItem key={b} onClick={() => changeBranch(b)} data-testid={`branch-option-${b.toLowerCase().replace(/\s/g,'-')}`}>
                    {b}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                  <Bell className="h-5 w-5 text-slate-600" />
                  {notifs.length > 0 && (
                    <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--yc-rose)]" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Alerts</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 ? (
                  <div className="px-3 py-6 text-sm text-slate-500 text-center">All clear.</div>
                ) : (
                  notifs.map((n, i) => (
                    <DropdownMenuItem key={i} className="py-2">
                      <span className={`h-2 w-2 rounded-full mr-2 ${n.tone === "red" ? "bg-[var(--yc-rose)]" : "bg-[var(--yc-gold)]"}`} />
                      {n.label}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="h-8 w-px bg-slate-200" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-slate-50 rounded-full pr-2 pl-1 py-1" data-testid="user-menu-trigger">
                  <div className="h-8 w-8 rounded-full bg-[var(--yc-navy)] text-[var(--yc-gold)] flex items-center justify-center font-heading font-bold text-sm">
                    {(hrSelf?.name || "?").charAt(0)}
                  </div>
                  <div className="text-left leading-tight">
                    <div className="text-sm font-medium text-[var(--yc-navy)]">{hrSelf?.name || "Select name"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">HR Team</div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500">Working as {hrSelf?.name || "—"}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={clearHR} data-testid="change-user-button">
                  <RefreshCcw className="h-4 w-4 mr-2" /> Change user
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
